import { useState } from "react";
import { Eye, ShieldCheck, Tag, UserRound, Users, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  useChildVisibility,
  useLeadVisibility,
  type VisibilityReason,
  type VisibilityReasonKind,
} from "@/features/leads/api/useVisibility";

/**
 * "Why can I see this?" icon rendered in the lead/child drawer header
 * (just before the edit button). Hovering the eye icon opens a tooltip
 * that lists every reason returned by `/core/leads/:id/visibility` (or
 * `/core/child/...`). Fetching is deferred until the first hover so the
 * list view doesn't pay an extra request per drawer mount.
 *
 * Tenant-admin shortcut: when the only reason is `tenant_admin`, the
 * tooltip just shows that one line — there is nothing else to explain.
 */

type Mode = "lead" | "child";

const REASON_ICON: Record<
  VisibilityReasonKind,
  React.ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  tenant_admin: ShieldCheck,
  stage_admin: ShieldCheck,
  stage_manage: Users,
  custom_field_manage: Tag,
  personal_assigned: UserRound,
  personal_via_child: UserRound,
  parent_lead_access: Eye,
};

interface Props {
  mode: Mode;
  id: number | string | undefined | null;
}

export default function VisibilityInfo({ mode, id }: Props) {
  // Latch this once `true` so React Query keeps the cached payload even
  // after the tooltip closes — the next hover is instant.
  const [hasOpened, setHasOpened] = useState(false);

  const leadQuery = useLeadVisibility(
    mode === "lead" ? id : undefined,
    mode === "lead" && hasOpened,
  );
  const childQuery = useChildVisibility(
    mode === "child" ? id : undefined,
    mode === "child" && hasOpened,
  );

  const data = mode === "lead" ? leadQuery.data : childQuery.data;
  const isLoading =
    mode === "lead" ? leadQuery.isLoading : childQuery.isLoading;
  const reasons = data?.reasons ?? [];

  return (
    <Tooltip
      delayDuration={150}
      onOpenChange={(open) => {
        if (open && !hasOpened) setHasOpened(true);
      }}
    >
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Why can I see this?"
          className="size-8 rounded-lg hover:bg-accent/50"
          style={{
            backgroundImage:
              "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
          }}
        >
          <Eye
            className="size-4 text-[var(--color-black-8)]"
            strokeWidth={1.5}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={6}
        className="max-w-[380px] p-0 bg-white text-[var(--color-text-primary)] border border-[#E5E7EB] shadow-lg"
        hideArrow
      >
        <div className="px-3 py-2 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-1.5">
            <Info className="size-3.5 text-[var(--color-black-7)]" />
            <p className="text-[12px] font-medium text-[#111827]">
              Why can I see this {mode === "lead" ? "lead" : "deal"}?
            </p>
          </div>
        </div>

        <div className="max-h-[280px] overflow-y-auto px-1.5 py-1.5">
          {isLoading && (
            <p className="text-[11px] text-[#6B7280] px-2 py-2">
              Checking your access…
            </p>
          )}

          {!isLoading && reasons.length === 0 && (
            <p className="text-[11px] text-[#6B7280] px-2 py-2">
              No specific reason returned.
            </p>
          )}

          {!isLoading &&
            reasons.map((r, i) => <ReasonRow key={i} reason={r} />)}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function ReasonRow({ reason }: { reason: VisibilityReason }) {
  const Icon = REASON_ICON[reason.kind] ?? Info;
  return (
    <div className="flex items-start gap-2 px-2 py-1.5 rounded-md">
      <span className="mt-0.5 inline-flex items-center justify-center rounded-full bg-[#F3F4F6] p-1">
        <Icon className="size-3.5 text-[#374151]" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <p className="text-[12px] font-medium text-[#111827] leading-snug">
          {reason.label}
        </p>
        {reason.detail && (
          <p className="text-[11px] text-[#6B7280] leading-snug mt-0.5">
            {reason.detail}
          </p>
        )}
      </div>
    </div>
  );
}
